/**
 * 参数元数据：模型 + `apm.pdef.json` 解析 + 校验 + 搜索。
 *
 * ## 为什么元数据只能来自上游的 `apm.pdef.json`
 *
 * MAVLink 有一个官方的参数元数据机制 `COMPONENT_INFORMATION` / `COMPONENT_METADATA`，
 * 但**ArduPilot 没有实现它**（`GCS_Common.cpp` 里没有任何 `COMPONENT_INFORMATION` 的处理，
 * 核对日期 2026-09-25）。所以"这个参数叫什么、单位是什么、能取哪些值"只能来自上游随固件
 * 发布的参数定义文件：`https://autotest.ardupilot.org/Parameters/<机型>/apm.pdef.json`。
 * 抓取与缓存见 `metadata-source.ts`；这里只管"拿到文本之后怎么用"。
 *
 * ## 解析策略（实测数据决定，不是拍脑袋）
 *
 * Copter 的 pdef 是 2,240,383 字节 / 5777 条参数 / 388 个分组。用**真实文件**在本机实测
 * （Node 22 / V8，Windows，多次测量取中位数）：
 *
 *   - `JSON.parse(整份文本)`：**5.9 ms**
 *   - `createParameterMetadataIndex(text)`（parse + 建"名字 → 原始记录"索引 + 分组计数）：**7.5 ms**
 *   - 惰性 `get()` 取 1000 条：**0.13 ms**；把 5777 条**全部**建模：9.0 ms
 *   - 浅搜索（名字/显示名，命中 50 条）：0.3 ms
 *   - 对照实验：手写字符扫描建索引（不 parse 值）要 **7.0 ms** —— **比 JSON.parse 还慢**
 *
 * 结论有两条，都是反直觉的：
 *
 * 1. **不要手写 JSON 扫描器"为了省一次 JSON.parse"**：V8 的解析器是编译期优化的原生代码，
 *    JS 字符循环打不过它。那种"惰性解析"只是白白增加 bug 面积。
 * 2. **真正的代价不是 CPU，而是建模与常驻内存**：全量建模要按住 5777 个对象 +
 *    每个枚举一张 Map（实测堆增量约 5 MB，只 parse 不建模约 3 MB）。
 *
 * 所以策略是"**整体 JSON.parse + 惰性建模**"：解析本身一次做完（7.5 ms），
 * 但只保留 `参数名 → (所属分组, 原始记录)` 的轻量索引，
 * `ParamMetadataEntry` 在被访问时才构造并缓存。界面的主路径
 * （列参数名、按分组过滤、按名字/显示名搜索）因此完全不碰那 1376 张枚举表。
 *
 * 另外，2.24 MB 的网络响应本身就是异步的，抓取时机由调用方掌握；
 * `load()` 之前不会有任何解析发生（见 `metadata-source.ts` 的懒加载语义），
 * 所以"连上机体"这条路径完全不付这份代价。
 */
import { paramValuesEqual } from './float';

// ---------------------------------------------------------------------------
// 模型
// ---------------------------------------------------------------------------

export interface ParamRange {
  readonly low: number;
  readonly high: number;
}

/** 一条参数的元数据。所有字段都可能缺失 —— 上游 pdef 的字段是可选的。 */
export interface ParamMetadataEntry {
  readonly name: string;
  /** 上游 `DisplayName`（英文短名）；缺失时退回参数名本身 */
  readonly displayName: string;
  /** 上游 `Description`（英文说明） */
  readonly description: string;
  /**
   * 中文说明。
   *
   * 上游 pdef **没有中文**，所以它只能来自注入的翻译表（见 `ParamTranslations`）
   * 或内置的一小份种子表。为 null 时界面必须回退到 `description`，
   * 而不是显示空白 —— "没有中文"不是"没有元数据"。
   */
  readonly descriptionZh: string | null;
  readonly units: string | null;
  readonly range: ParamRange | null;
  /** 枚举值：数值 → 上游给的文本 */
  readonly values: ReadonlyMap<number, string> | null;
  /** 位掩码：位序号 → 上游给的文本 */
  readonly bitmask: ReadonlyMap<number, string> | null;
  readonly readOnly: boolean;
  readonly rebootRequired: boolean;
  /** 上游 `User`（`Standard` / `Advanced` / `Beta`…） */
  readonly user: string | null;
  /** 上游 `Increment`（界面步进按钮用） */
  readonly increment: number | null;
  /** 上游 pdef 的分组（顶层键）；空串是"未分组" */
  readonly group: string;
  /** 分组的中文名；没有已知译名时等于 `group`（未分组时为"未分组"） */
  readonly groupLabel: string;
}

export interface ParamGroupInfo {
  readonly id: string;
  readonly label: string;
  readonly count: number;
}

export interface ParamSearchOptions {
  /** 返回条数上限；默认 50 */
  readonly limit?: number;
  /** 限定分组 */
  readonly group?: string;
  /**
   * 是否连说明文本一起搜。
   *
   * 关掉（默认）只搜参数名与显示名，代价与分组过滤同级；
   * 打开会**建模**被扫到的条目（构造枚举 Map），因此慢一些 ——
   * 界面上的"搜索框"用默认值，"高级搜索"才打开它。
   */
  readonly deep?: boolean;
}

export interface ParamTranslations {
  /** 分组名 → 中文 */
  readonly groups?: Readonly<Record<string, string>>;
  /** 参数名 → 中文说明 */
  readonly params?: Readonly<Record<string, string>>;
}

/**
 * 合并内置种子表与注入的译表（注入优先）。
 *
 * 抽出来是为了让**审计**（`translation-audit.ts`）与**建索引**用同一个合并规则。
 * 审计如果自己再写一遍 `{...种子, ...注入}`，两边迟早会在"注入的 `undefined` 值算不算覆盖"
 * 这类细节上分叉，而分叉的表现是"审计说覆盖了、界面却没有中文"——
 * 一份专门用来发现覆盖问题的报告，自己先给出错误的覆盖结论，就没有存在的意义了。
 */
export function mergeParamTranslations(
  translations: ParamTranslations | null | undefined,
): { readonly params: Record<string, string>; readonly groups: Record<string, string> } {
  return {
    params: mergeTables(PARAM_DESCRIPTION_ZH_SEED, translations?.params),
    groups: mergeTables(PARAM_GROUP_LABELS_ZH, translations?.groups),
  };
}

export type ParamMetadataSourceStatus =
  /** 刚从网络取回 */
  | 'network'
  /** 来自本地缓存 */
  | 'cache'
  /** 缓存已过期（但仍然可用，后台会尝试刷新） */
  | 'stale-cache'
  /** 直接由代码构造（内置快照 / 测试 fixture） */
  | 'inline'
  /** 拿不到元数据（离线降级）—— 一切照常工作，只是没有说明与范围校验 */
  | 'empty';

export interface ParamMetadataIndex {
  readonly size: number;
  readonly source: ParamMetadataSourceStatus;
  /** 拿不到元数据的原因（`source === 'empty'` 时给界面提示用），否则为 null */
  readonly unavailableReason: string | null;
  has(name: string): boolean;
  get(name: string): ParamMetadataEntry | undefined;
  /** 全部参数名（保持 pdef 里的先后顺序，界面自己排序） */
  names(): readonly string[];
  groups(): readonly ParamGroupInfo[];
  inGroup(group: string): readonly ParamMetadataEntry[];
  search(query: string, options?: ParamSearchOptions): readonly ParamMetadataEntry[];
}

// ---------------------------------------------------------------------------
// 中文标签
// ---------------------------------------------------------------------------

/**
 * 常见分组的中文名。
 *
 * 只写"有把握"的：这些前缀在 ArduPilot 里的含义是稳定的、可以从上游库名直接对应的
 * （`INS_` → `AP_InertialSensor`、`BATT_` → `AP_BattMonitor`…）。
 * 没把握的一律不写 —— 界面回退显示原始分组名，比编一个错的中文名安全。
 */
export const PARAM_GROUP_LABELS_ZH: Readonly<Record<string, string>> = Object.freeze({
  '': '未分组',
  ADSB_: 'ADS-B 接收',
  AFS_: '高级失速保护',
  AHRS_: '姿态航向参考',
  AIS_: '船舶识别（AIS）',
  ARMING_: '解锁前检查',
  AROT_: '直升机自转下降',
  ARSPD: '空速计',
  ARSPD_: '空速计',
  ATC_: '姿态控制',
  AUTOTUNE_: '自动调参',
  AVD_: 'ADS-B 避让',
  AVOID_: '避障',
  BARO: '气压计',
  BATT_: '电池监视',
  BCN: '信标',
  BRD_: '飞控板',
  BRD_RADIO: '板载电台',
  BRD_RTC: '板载实时时钟',
  BTN_: '按钮',
  CAM: '相机/云台触发',
  CAM1: '相机/云台触发 1',
  CAM2: '相机/云台触发 2',
  CAN_: 'CAN 总线',
  CHUTE_: '降落伞',
  CIRCLE_: '绕圈模式',
  COMPASS_: '罗盘',
  CUST_ROT: '自定义电机布局',
  Copter: 'ArduCopter 专有参数',
  Plane: 'ArduPlane 专有参数',
  Rover: 'ArduRover 专有参数',
  Sub: 'ArduSub 专有参数',
  Blimp: 'ArduBlimp 专有参数',
  Tracker: '天线跟踪专有参数',
  DDS: 'DDS 通信',
  EFI: '电喷发动机',
  EK2_: 'EKF2 状态估计',
  EK3_: 'EKF3 状态估计',
  FENCE_: '地理围栏',
  FFT_: '振动频谱分析',
  FHLD: '跟随模式',
  FILT: '滤波器',
  FLOW: '光流',
  FOLL: '跟随',
  FRSKY_: 'FrSky 遥测',
  GEN_: '通用设置',
  GPS: 'GPS',
  GRIP_: '抓取器',
  H_: '直升机',
  INS: '惯性导航（加速度计/陀螺）',
  LAND_: '降落',
  LGR_: '起落架',
  LOG: '日志',
  LOIT_: '悬停模式',
  MAV: 'MAVLink 转发',
  MIS_: '任务',
  MNT1: '云台 1',
  MNT2: '云台 2',
  MOT_: '电机',
  MSP: 'MSP 协议',
  NET_: '网络',
  NTF_: '通知与 LED',
  OSD: 'OSD 屏显',
  PHLD: '定点保持',
  PSC: '位置与速度控制',
  RALLY_: '集结点',
  RC: '遥控输入',
  RCMAP_: '遥控通道映射',
  RELAY: '继电器',
  RNGFND: '测距仪',
  RPM: '转速计',
  RSSI_: '信号强度',
  RTL_: '返航',
  SCHED_: '调度器',
  SCR_: '脚本',
  SERIAL: '串口',
  SERVO: '舵机输出',
  SIM_: '仿真',
  SRTL_: '智能返航',
  STAT: '运行统计',
  TCAL: '温度校准',
  TEMP: '温度传感器',
  TERRAIN_: '地形跟随',
  TMODE: '调参模式',
  VISO: '视觉定位',
  VTX_: '图传',
  WINCH: '绞盘',
  WP_: '航点',
  WPNAV_: '自主导航',
  WVANE_: '风向标',
  ZIGZ_: '之字形航线',
});

/**
 * 中文说明的**种子表**。
 *
 * 完整的中文参数说明不该由 `@hgc/core` 承担（5777 条英文说明需要翻译与校对，
 * 而且它属于文案而不是运行时逻辑），所以这里的定位是：
 *   - 证明"注入翻译表 → 界面拿到 `descriptionZh`"这条管线是通的、有测试覆盖的；
 *   - 顺手给几条最常被操作的参数提供中文，装机首次联调时不会满屏英文。
 * 权威来源是应用层注入的 `ParamTranslations.params`，与之合并（注入优先）。
 *
 * **注意：参数名会随固件版本变化。** 核对上游文件时发现，当前 master 的 Copter pdef
 * 已经把一些参数改成了带单位后缀的名字（`RTL_ALT` → `RTL_ALT_M`、`RTL_SPEED` → `RTL_SPEED_MS`），
 * 而发布版固件（4.6/4.7）用的是老名字。这张表按发布版固件的名字写，
 * 因此对着 master 的 pdef 用会有很多条匹配不上（这只是"没有中文"，不会出错）。
 * 需要长期稳定的话，界面应当按**固件版本**注入对应的翻译表，而不是依赖内置种子。
 */
export const PARAM_DESCRIPTION_ZH_SEED: Readonly<Record<string, string>> = Object.freeze({
  ARMING_CHECK: '解锁前的自检项位掩码：每一位对应一项检查（如 GPS、罗盘、气压计、电池等），关掉某一位即跳过该项检查，会显著降低飞行安全性。',
  BATT_CAPACITY: '电池额定容量（mAh），用于估算剩余电量与续航。',
  BATT_LOW_VOLT: '低电量告警电压（V）。低于该电压时触发低电量提示。',
  FENCE_ACTION: '地理围栏被触发时执行的动作（报告 / 返航 / 降落 / 刹车等）。',
  FENCE_ENABLE: '是否启用地理围栏。关闭后飞机不会在越过围栏边界时被拦下。',
  FENCE_RADIUS: '圆柱形围栏的半径（m），以返航点为中心。',
  FLTMODE1: '遥控飞行模式开关在位置 1 时对应的飞行模式编号。',
  LOG_BITMASK: '机载日志记录项位掩码：每一位对应一类日志（姿态、GPS、PID 调参、原始 IMU 等）。',
  MOT_SPIN_ARM: '解锁后电机的怠速油门比例。过大在解锁瞬间会产生危险推力，过小可能导致电机不转。',
  RTL_ALT: '返航高度（cm），相对返航点的高度；返航时先爬升/下降到该高度再飞回。',
  WPNAV_SPEED: '自主飞行（自动模式/返航等）的水平目标速度（cm/s）。',
});

// ---------------------------------------------------------------------------
// 上游 pdef 的原始形状（字段全是可选的、且数值可能以字符串给出）
// ---------------------------------------------------------------------------

/** pdef 里数值字段用字符串表示是常态（`"Range": {"low": "800"}`），所以统一走这里转换。 */
function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toText(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * pdef 的布尔字段。
 *
 * **坑**：真实的 `apm.pdef.json` 里 `ReadOnly` / `RebootRequired` 的值是
 * **字符串 `"True"`**，不是布尔 `true`（核对日期 2026-09-25；
 * 当前 Copter 文件里 77 条 `ReadOnly="True"`、913 条 `RebootRequired="True"`）。
 * 只判 `=== true` 会让这两个功能**在真实数据上永不生效**：
 * 只读参数照样被发出去、然后白等一次超时；用户也永远看不到"需要重启"的提示。
 * 而 fixtures 用布尔值时测试偏偏是绿的 —— 这就是为什么这里必须两种形态都收。
 */
function toFlag(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase();
    return text === 'true' || text === '1' || text === 'yes' || text === 'on';
  }
  return false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `{ "0": "Fast Attitude", ... }` → `Map(0 → "Fast Attitude")`；键不是数字的条目丢掉 */
function toNumberKeyedMap(value: unknown): ReadonlyMap<number, string> | null {
  if (!isRecord(value)) return null;
  const map = new Map<number, string>();
  for (const [key, label] of Object.entries(value)) {
    const numeric = toFiniteNumber(key);
    if (numeric === null || typeof label !== 'string') continue;
    map.set(numeric, label);
  }
  return map.size === 0 ? null : map;
}

function toRange(value: unknown): ParamRange | null {
  if (!isRecord(value)) return null;
  const low = toFiniteNumber(value['low']);
  const high = toFiniteNumber(value['high']);
  if (low === null || high === null) return null;
  return { low, high };
}

type RawPdefEntry = Record<string, unknown>;

interface MetadataSlot {
  readonly name: string;
  readonly group: string;
  readonly raw: RawPdefEntry;
}

// ---------------------------------------------------------------------------
// 解析
// ---------------------------------------------------------------------------

/** 元数据文本不是合法 JSON 时抛出。抓取失败/HTML 错误页会走到这里。 */
export class ParamMetadataParseError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'ParamMetadataParseError';
  }
}

export interface CreateParamMetadataIndexOptions {
  readonly source?: ParamMetadataSourceStatus;
  readonly translations?: ParamTranslations | null;
  /** 离线降级时说明"为什么没有元数据" */
  readonly unavailableReason?: string | null;
}

/**
 * 解析 `apm.pdef.json` 文本，得到一个按需建模的元数据索引。
 *
 * 真实文件的顶层结构是 `分组名 → 参数名 → 参数定义`（**没有**机型外层包装，
 * 机型只体现在 URL 与缓存键里）。分组名有的带下划线（`INS_`），有的不带
 * （`GPS`、`Copter`），还有空串；所以分组一律以 pdef 自己给的为准，
 * 不用"名字前缀"去猜 —— 例如 `LOG_BITMASK` 的分组是 `Copter` 而不是 `LOG`。
 */
export function createParameterMetadataIndex(
  text: string,
  options: CreateParamMetadataIndexOptions = {},
): ParamMetadataIndex {
  const translations = options.translations ?? null;
  const { params: paramsZh, groups: groupsZh } = mergeParamTranslations(translations);

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new ParamMetadataParseError(
      `参数元数据不是合法 JSON（${text.length} 字节）：${error instanceof Error ? error.message : String(error)}`,
      { cause: error },
    );
  }
  if (!isRecord(parsed)) {
    throw new ParamMetadataParseError('参数元数据的顶层必须是 JSON 对象');
  }

  const slots = new Map<string, MetadataSlot>();
  const counts = new Map<string, number>();
  for (const group of Object.keys(parsed)) {
    const bucket = parsed[group];
    if (!isRecord(bucket)) continue;
    for (const name of Object.keys(bucket)) {
      const raw = bucket[name];
      // pdef 里混着非参数条目（例如 `"json": { "version": 0 }`）——
      // 它们不是对象，直接跳过；静默跳过是安全的，因为这里的取值只影响"有没有元数据"
      if (!isRecord(raw)) continue;
      slots.set(name, { name, group, raw });
      counts.set(group, (counts.get(group) ?? 0) + 1);
    }
  }

  const cache = new Map<string, ParamMetadataEntry>();

  const materialize = (slot: MetadataSlot): ParamMetadataEntry => {
    const cached = cache.get(slot.name);
    if (cached !== undefined) return cached;
    const raw = slot.raw;
    const entry: ParamMetadataEntry = {
      name: slot.name,
      displayName: toText(raw['DisplayName']) ?? slot.name,
      description: toText(raw['Description']) ?? '',
      descriptionZh: paramsZh[slot.name] ?? null,
      units: toText(raw['Units']),
      range: toRange(raw['Range']),
      values: toNumberKeyedMap(raw['Values']),
      bitmask: toNumberKeyedMap(raw['Bitmask']),
      readOnly: toFlag(raw['ReadOnly']),
      rebootRequired: toFlag(raw['RebootRequired']),
      user: toText(raw['User']),
      increment: toFiniteNumber(raw['Increment']),
      group: slot.group,
      groupLabel: groupsZh[slot.group] ?? (slot.group.length === 0 ? '未分组' : slot.group),
    };
    cache.set(slot.name, entry);
    return entry;
  };

  const groupList: ParamGroupInfo[] = [...counts.entries()]
    .map(([id, count]) => ({ id, label: groupsZh[id] ?? (id.length === 0 ? '未分组' : id), count }))
    .sort((a, b) => (a.count === b.count ? (a.id < b.id ? -1 : 1) : b.count - a.count));

  const source = options.source ?? 'inline';
  const reason = options.unavailableReason ?? null;

  return {
    size: slots.size,
    source,
    unavailableReason: reason,
    has: (name) => slots.has(name),
    get: (name) => {
      const slot = slots.get(name);
      return slot === undefined ? undefined : materialize(slot);
    },
    names: () => [...slots.keys()],
    groups: () => groupList,
    inGroup: (group) => {
      const out: ParamMetadataEntry[] = [];
      for (const slot of slots.values()) {
        if (slot.group === group) out.push(materialize(slot));
      }
      return out;
    },
    search: (query, searchOptions = {}) => {
      const needle = query.trim().toUpperCase();
      if (needle.length === 0) return [];
      const limit = searchOptions.limit ?? 50;
      const wantedGroup = searchOptions.group;
      const deep = searchOptions.deep ?? false;
      const out: ParamMetadataEntry[] = [];
      for (const slot of slots.values()) {
        if (out.length >= limit) break;
        if (wantedGroup !== undefined && slot.group !== wantedGroup) continue;
        // 浅搜索只碰字符串（DisplayName 直接在原始记录里），不建模
        const displayName = toText(slot.raw['DisplayName']) ?? slot.name;
        if (slot.name.toUpperCase().includes(needle) || displayName.toUpperCase().includes(needle)) {
          out.push(materialize(slot));
          continue;
        }
        if (!deep) continue;
        const entry = materialize(slot);
        if (matchesDescription(entry, needle)) out.push(entry);
      }
      return out;
    },
  };
}

function matchesDescription(entry: ParamMetadataEntry, needle: string): boolean {
  if (entry.description.toUpperCase().includes(needle)) return true;
  if (entry.descriptionZh !== null && entry.descriptionZh.includes(needle)) return true;
  if (entry.groupLabel.toUpperCase().includes(needle)) return true;
  return false;
}

function mergeTables(
  base: Readonly<Record<string, string>>,
  extra: Readonly<Record<string, string>> | undefined,
): Record<string, string> {
  return extra === undefined ? { ...base } : { ...base, ...extra };
}

/**
 * 空索引：**离线降级**用。
 *
 * 拿不到元数据不是错误状态：参数照常下载、照常读写、照常显示，
 * 只是没有中文说明、没有取值范围提示、没有只读标记。
 * 因此这个索引的每个方法都必须在"什么都没有"的情况下返回合理结果，
 * 而不是抛错或让上层到处判空。
 */
export function createEmptyMetadataIndex(reason: string | null = null): ParamMetadataIndex {
  return {
    size: 0,
    source: 'empty',
    unavailableReason: reason,
    has: () => false,
    get: () => undefined,
    names: () => [],
    groups: () => [],
    inGroup: () => [],
    search: () => [],
  };
}

// ---------------------------------------------------------------------------
// 校验
// ---------------------------------------------------------------------------

export type ParamValidationIssueKind =
  | 'read-only'
  | 'invalid-value'
  | 'below-min'
  | 'above-max'
  | 'not-in-enum'
  | 'unknown-bit'
  | 'unknown-parameter'
  /** 元数据说这个参数需要重启才生效 */
  | 'rebootRequired';

export interface ParamValidationIssue {
  readonly kind: ParamValidationIssueKind;
  readonly message: string;
}

export interface ParamValidationResult {
  /**
   * 阻断性问题。非 null 时调用方**不要**发送 `PARAM_SET`：
   * 只读参数写进去只会被飞控静默忽略，然后白等一次超时；
   * 非有限数则会让组帧直接抛错。
   */
  readonly blocking: 'read-only' | 'invalid-value' | null;
  readonly blockingMessage: string | null;
  /** 不阻断但必须告诉用户的问题（超范围、非法枚举值…） */
  readonly warnings: readonly ParamValidationIssue[];
}

/**
 * 用元数据校验一个待写入的值。
 *
 * 范围与枚举只给**警告不给拒绝**：ArduPilot 对超范围的值通常是钳制而不是拒绝，
 * 用户有时确实需要写入一个"看起来越界"的值（上游 Range 是给界面滑条用的建议区间，
 * 不完全等于飞控的硬限制）。真正的判据永远是飞控回读的值 —— 见 `manager.ts` 的确认逻辑。
 * 只有"只读"与"非法数"是硬阻断，因为前者必然失败、后者根本发不出去。
 */
export function validateParamValue(
  value: number,
  metadata: ParamMetadataEntry | null,
): ParamValidationResult {
  const warnings: ParamValidationIssue[] = [];

  if (!Number.isFinite(value)) {
    return {
      blocking: 'invalid-value',
      blockingMessage: `参数值必须是有限数，收到 ${String(value)}`,
      warnings,
    };
  }

  if (metadata === null) {
    // 元数据缺失只影响提示，不影响写入 —— 离线降级时必须照常工作
    warnings.push({
      kind: 'unknown-parameter',
      message: '没有该参数的元数据，无法校验取值范围（元数据未加载或上游没有这条参数）',
    });
    return { blocking: null, blockingMessage: null, warnings };
  }

  if (metadata.readOnly) {
    return {
      blocking: 'read-only',
      blockingMessage: `${metadata.name} 在固件里是只读参数，写进去会被飞控忽略（未发送）`,
      warnings,
    };
  }

  const range = metadata.range;
  if (range !== null) {
    // 边界判等也走浮点容差：Range 是从文本读回来的，可能比 float32 的值稍小
    if (!paramValuesEqual(value, range.low) && value < range.low) {
      warnings.push({
        kind: 'below-min',
        message: `低于取值下限 ${range.low}（当前 ${value}），飞控可能会钳制到下限`,
      });
    } else if (!paramValuesEqual(value, range.high) && value > range.high) {
      warnings.push({
        kind: 'above-max',
        message: `高于取值上限 ${range.high}（当前 ${value}），飞控可能会钳制到上限`,
      });
    }
  }

  const values = metadata.values;
  if (values !== null) {
    if (!values.has(value) && !values.has(Math.round(value))) {
      warnings.push({
        kind: 'not-in-enum',
        message: `${value} 不是 ${metadata.name} 的合法枚举值（合法值：${[...values.keys()].slice(0, 8).join('/')}…）`,
      });
    }
  }

  const bitmask = metadata.bitmask;
  if (bitmask !== null && Number.isInteger(value) && value >= 0 && value <= 0xffffffff) {
    let known = 0;
    for (const bit of bitmask.keys()) {
      if (Number.isInteger(bit) && bit >= 0 && bit < 32) known |= 1 << bit;
    }
    if ((value & ~known) !== 0) {
      warnings.push({
        kind: 'unknown-bit',
        message: `位掩码里含未定义的位（${value & ~known}），固件可能忽略这些位`,
      });
    }
  }

  if (metadata.rebootRequired) {
    warnings.push({
      kind: 'rebootRequired',
      message: '该参数需要重启飞控后才生效',
    });
  }

  return { blocking: null, blockingMessage: null, warnings };
}
